import React, { useState, useRef } from 'react';
import { Badge, Box } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { FavoriteDropdownWithStorage } from '../../../../components/favorite-dropdown';
import { ColorPalette } from '../../../../components/config/color-palette';
import { authorityOpticStorage } from '../services/authority-optic-storage';
import { AUTHORITY_OPTIC_BESKAEFTIGELSE_STANDARD, AUTHORITY_OPTIC_SOCIAL_STANDARD, AuthorityOpticId } from './authority-optic-context';

export interface AuthorityOpticOption {
  id: AuthorityOpticId;
  label: string;
}

export const AUTHORITY_OPTIC_OPTIONS: AuthorityOpticOption[] = [
  { id: AUTHORITY_OPTIC_BESKAEFTIGELSE_STANDARD, label: 'Beskæftigelse - standard' },
  { id: AUTHORITY_OPTIC_SOCIAL_STANDARD, label: 'Social - standard' },
];

const EXPANDED_WIDTH = 280;
const ICON_SIZE = 24;

export interface IAuthorityOpticDropdown {
  selectedOpticId: AuthorityOpticId | undefined;
  onSelectOptic: (opticId: AuthorityOpticId) => void;
  label?: string;
  availableOptics?: string[] | undefined;
  badgeLetterByOptic?: Record<string, string>;
}

export function AuthorityOpticDropdown(props: IAuthorityOpticDropdown) {
  const { selectedOpticId, onSelectOptic, label = 'Kontekst', availableOptics, badgeLetterByOptic } = props;
  const [hover, setHover] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  function handleMouseEnter() {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setHover(true);
  }

  // The 10px matches the badge pill's right overflow (50% of ~20px default MUI badge width).
  // Now the hover area covers the badge in collapsed state.
  // The debounce is still there as a safety net to avoid incessive flickering when moving the mouse along edges.
  function handleMouseLeave() {
    closeTimerRef.current = setTimeout(() => setHover(false), 100);
  }

  // Filter options based on availableOptics if provided, otherwise show all
  const filteredOptions = React.useMemo(() => {
    if (!availableOptics || availableOptics.length === 0) {
      return AUTHORITY_OPTIC_OPTIONS;
    }
    return AUTHORITY_OPTIC_OPTIONS.filter(option => availableOptics.includes(option.id));
  }, [availableOptics]);

  // Sort options alphabetically by label
  const sortedOptions = [...filteredOptions].sort((a, b) => a.label.localeCompare(b.label));

  function handleSelectOptic(option: AuthorityOpticOption) {
    onSelectOptic(option.id);
    setHover(false);
  }

  const dropdown = (
    <FavoriteDropdownWithStorage
      items={sortedOptions}
      selectedItemId={selectedOpticId}
      onSelectItem={handleSelectOptic}
      storage={{
        getFavoriteId: authorityOpticStorage.getFavoriteOpticId,
        setFavoriteId: authorityOpticStorage.setFavoriteOpticId,
        clearFavoriteId: authorityOpticStorage.clearFavoriteOpticId,
      }}
      getItemId={option => option.id}
      getItemLabel={option => option.label}
      label={label}
      minWidth={EXPANDED_WIDTH}
    />
  );

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minWidth: ICON_SIZE,
        paddingRight: '10px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: ColorPalette.WHITE,
          flexShrink: 0,
        }}
        aria-hidden
      >
        <Badge
          badgeContent={selectedOpticId ? badgeLetterByOptic?.[selectedOpticId] : undefined}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
        >
          <Visibility sx={{ fontSize: ICON_SIZE }} />
        </Badge>
      </Box>
      <Box
        sx={{
          maxWidth: hover ? EXPANDED_WIDTH : 0,
          overflow: 'hidden',
          opacity: hover ? 1 : 0,
          transition: 'max-width 0.2s ease-out, opacity 0.15s ease-out',
        }}
      >
        {dropdown}
      </Box>
    </Box>
  );
}
